import { useEffect, useState } from "react";
import { Crown, Trophy, Swords, Shield, Gem, Medal } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIER_ICON = { shield: Shield, medal: Medal, gem: Gem, crown: Crown };

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [arena, setArena] = useState(null);
  const [arenaExam, setArenaExam] = useState("CPNS");
  const [tab, setTab] = useState("exp");
  const { user } = useAuth();

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setRows(data.leaderboard)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/arena/ranks?exam=${arenaExam}`).then(({ data }) => setArena(data)).catch(() => {});
  }, [arenaExam]);

  const medal = (rank) =>
    rank === 1 ? "bg-su-secondary text-black" : rank === 2 ? "bg-slate-300 text-black" : rank === 3 ? "bg-orange-400 text-white" : "bg-su-track text-su-muted";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl flex items-center gap-2"><Trophy className="w-7 h-7 text-su-secondary" /> Peringkat</h1>
        <p className="text-su-muted font-medium">Bersaing di level nasional.</p>
      </div>

      <div className="flex gap-2">
        <button data-testid="lb-tab-exp" onClick={() => setTab("exp")}
          className={`px-4 py-2.5 rounded-2xl font-heading font-extrabold text-sm border-2 ${tab === "exp" ? "bg-su-primary text-white border-su-primaryShadow" : "bg-su-surface text-su-muted border-su-border"}`}>EXP Nasional</button>
        <button data-testid="lb-tab-arena" onClick={() => setTab("arena")}
          className={`px-4 py-2.5 rounded-2xl font-heading font-extrabold text-sm border-2 ${tab === "arena" ? "bg-su-danger text-white border-su-dangerShadow" : "bg-su-surface text-su-muted border-su-border"}`}>Arena ELO</button>
      </div>

      {tab === "exp" && (
        <>
          <div className="grid grid-cols-3 gap-3 items-end">
            {[rows[1], rows[0], rows[2]].map((r, i) => {
              if (!r) return <div key={i} />;
              const heights = ["h-24", "h-32", "h-20"];
              return (
                <div key={r.rank} className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-su-primary/15 grid place-items-center font-heading font-black text-su-primary text-lg mb-2">{r.name?.[0] || "?"}</div>
                  <p className="font-heading font-extrabold text-sm truncate flex items-center justify-center gap-1">{r.name}{r.vip && <Crown className="w-3.5 h-3.5 text-su-secondary fill-current" />}</p>
                  <p className="text-xs text-su-muted font-bold mb-1">{r.exp.toLocaleString()} EXP</p>
                  <div className={`${heights[i]} rounded-t-2xl ${medal(r.rank)} grid place-items-center font-heading font-black text-2xl`}>{r.rank}</div>
                </div>
              );
            })}
          </div>
          <div className="card-chunky p-3 sm:p-5">
            <div className="space-y-2">
              {rows.map((r) => {
                const isMe = user && r.name === user.name;
                return (
                  <div key={r.rank} data-testid={`leaderboard-row-${r.rank}`}
                    className={`flex items-center gap-3 rounded-2xl p-3 border-2 ${isMe ? "border-su-primary bg-su-primary/5" : "border-su-border"}`}>
                    <span className={`w-9 h-9 grid place-items-center rounded-xl font-heading font-black text-sm ${medal(r.rank)}`}>{r.rank}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-extrabold truncate flex items-center gap-1">{r.name}{r.vip && <Crown className="w-3.5 h-3.5 text-su-secondary fill-current" />}{isMe && <span className="text-xs text-su-primary">(kamu)</span>}</p>
                      <p className="text-xs text-su-muted font-bold">Level {r.level}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold text-su-primary">{r.exp.toLocaleString()}</p>
                      <p className="text-[11px] text-su-muted font-bold flex items-center gap-1 justify-end"><Swords className="w-3 h-3" /> {r.elo}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {tab === "arena" && arena && <ArenaRanks arena={arena} arenaExam={arenaExam} setArenaExam={setArenaExam} />}
    </div>
  );
}

function ArenaRanks({ arena, arenaExam, setArenaExam }) {
  const me = arena.me;
  const MeIcon = TIER_ICON[me.icon] || Shield;
  const [season, setSeason] = useState(null);
  useEffect(() => { api.get("/season").then(({ data }) => setSeason(data)).catch(() => {}); }, []);
  const badges = season?.season_badges || [];
  return (
    <div className="space-y-6">
      {/* exam toggle */}
      <div className="flex gap-2" data-testid="arena-exam-tabs">
        {["CPNS", "SNBT"].map((ex) => (
          <button key={ex} data-testid={`arena-lb-${ex}`} onClick={() => setArenaExam(ex)}
            className={`px-4 py-2 rounded-2xl font-heading font-black text-sm border-2 transition-all ${arenaExam === ex ? "bg-su-danger text-white border-su-dangerShadow" : "bg-su-surface text-su-muted border-su-border"}`}>
            Arena {ex}
          </button>
        ))}
      </div>
      {/* season banner */}
      {season && (
        <div className="card-chunky p-4 flex items-center justify-between flex-wrap gap-2" data-testid="season-banner">
          <span className="font-heading font-black flex items-center gap-2"><Trophy className="w-5 h-5 text-su-secondary" /> Season {season.season}</span>
          {badges.length > 0 && (
            <div className="flex items-center gap-1.5" data-testid="season-badges">
              {badges.slice(-5).map((b, i) => (
                <span key={i} title={`Season ${b.season} · ${b.tier}`} className="text-[10px] font-heading font-black px-2 py-1 rounded-full border-2" style={{ borderColor: b.color, color: b.color }}>{b.tier} S{b.season}</span>
              ))}
            </div>
          )}
        </div>
      )}
      {/* last week champion banner */}
      {arena.champion && (
        <div className="card-chunky p-4 flex items-center gap-3 border-su-secondary/40 bg-su-secondary/5" data-testid="arena-champion-banner">
          <div className="w-11 h-11 rounded-2xl bg-su-secondary/20 grid place-items-center shrink-0"><Crown className="w-6 h-6 text-su-secondary fill-current" /></div>
          <div className="min-w-0">
            <p className="text-[11px] font-heading font-black uppercase text-su-secondary">Juara Arena {arenaExam} · Pekan {arena.champion.week_id}</p>
            <p className="font-heading font-black truncate">{arena.champion.name} <span className="text-su-muted font-mono text-sm">· {arena.champion.elo} ELO</span></p>
            {arena.champion.reward && (
              <p className="text-xs font-bold text-su-muted mt-0.5">🎁 Hadiah: {arena.champion.reward.tokens} token + {arena.champion.reward.energy} energi</p>
            )}
          </div>
        </div>
      )}

      {/* my champion badges */}
      {arena.my_champion_badges?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="my-champion-badges">
          <span className="text-xs font-heading font-black text-su-muted">Lencana juaramu:</span>
          {arena.my_champion_badges.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-[11px] font-heading font-black px-2 py-1 rounded-full bg-su-secondary/15 text-su-secondary border-2 border-su-secondary/40">
              <Crown className="w-3 h-3 fill-current" /> Juara {b.exam} {b.week_id}
            </span>
          ))}
        </div>
      )}
      <div className="card-chunky p-6" data-testid="my-tier">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl grid place-items-center" style={{ background: `${me.tier_color}22` }}>
            <MeIcon className="w-9 h-9" style={{ color: me.tier_color }} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-heading font-black uppercase text-su-muted">Tier kamu · Peringkat #{me.global_rank}</p>
            <h2 className="font-heading font-black text-2xl" style={{ color: me.tier_color }}>{me.tier}</h2>
            <p className="font-mono font-bold text-su-muted text-sm">{me.elo} ELO</p>
          </div>
        </div>
        {me.next_tier && (
          <div className="mt-4">
            <div className="flex justify-between text-xs font-bold text-su-muted mb-1">
              <span>Menuju {me.next_tier}</span><span>{me.next_at} ELO</span>
            </div>
            <div className="h-3 rounded-full bg-su-track overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.round(me.progress * 100)}%`, background: me.tier_color }} />
            </div>
          </div>
        )}
      </div>

      {/* tier legend */}
      <div className="flex flex-wrap gap-2">
        {arena.tiers.map((t) => { const I = TIER_ICON[t.icon] || Shield; return (
          <span key={t.name} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading font-black border-2" style={{ borderColor: t.color, color: t.color }}>
            <I className="w-3.5 h-3.5" /> {t.name} <span className="text-su-muted font-mono">{t.min}+</span>
          </span>
        ); })}
      </div>

      {/* global elo board */}
      <div className="card-chunky p-3 sm:p-5">
        <div className="space-y-2">
          {arena.leaderboard.map((r) => (
            <div key={r.rank} data-testid={`arena-rank-${r.rank}`}
              className={`flex items-center gap-3 rounded-2xl p-3 border-2 ${r.is_me ? "border-su-danger bg-su-danger/5" : "border-su-border"}`}>
              <span className="w-9 h-9 grid place-items-center rounded-xl font-heading font-black text-sm bg-su-track text-su-muted">{r.rank === 1 ? <Crown className="w-4 h-4 text-su-secondary fill-current" /> : r.rank}</span>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-extrabold truncate flex items-center gap-1">{r.name}{r.vip && <Crown className="w-3.5 h-3.5 text-su-secondary fill-current" />}{r.is_me && <span className="text-xs text-su-danger">(kamu)</span>}</p>
                <p className="text-xs font-bold" style={{ color: r.tier_color }}>{r.tier}</p>
              </div>
              <p className="font-mono font-bold text-su-danger flex items-center gap-1"><Swords className="w-3.5 h-3.5" /> {r.elo}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

